import type { ComponentPublicInstance } from 'vue'

import type { Agent } from '@newrelic/browser-agent/loaders/agent'
import type { AxiosError } from 'axios'

import { isNoise, type NormalizedError } from './errorNoiseFilters'

class AppErrorService {
  private static instance: AppErrorService | null = null
  private reportToNewRelic = false
  private newRelicInstance: Agent | null = null

  private constructor() {}

  static getInstance(): AppErrorService {
    if (!AppErrorService.instance) {
      AppErrorService.instance = new AppErrorService()
    }
    return AppErrorService.instance
  }

  initialize(reportToNewRelic: boolean, newRelicInstance: Agent | null): void {
    this.reportToNewRelic = reportToNewRelic
    this.newRelicInstance = newRelicInstance
  }

  reportVueError(
    err: unknown,
    vm: ComponentPublicInstance | null,
    info: string,
    customAttributes?: Record<string, unknown>
  ) {
    const message = err instanceof Error ? err.message : String(err)
    const normalized: NormalizedError = {
      source: 'vue',
      message,
      reason: err,
      stack: err instanceof Error ? (err.stack ?? null) : null
    }
    if (isNoise(normalized)) return

    let vueComponentName: string | null = null

    if (vm?.$options?.name) {
      vueComponentName = vm.$options.name
    }

    console.error('Error:', err)
    console.error('Vue component:', vm)
    console.error('Additional info:', info)
    if (vueComponentName) {
      console.error('Vue component Name:', vueComponentName)
    }

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = err instanceof Error ? err : new Error(String(err))
      this.newRelicInstance.noticeError(errorToReport, {
        vueComponentName,
        ...customAttributes
      })
    }
  }

  reportUnhandledPromiseRejection(
    event: PromiseRejectionEvent,
    customAttributes?: Record<string, unknown>
  ) {
    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason)
    const normalized: NormalizedError = {
      source: 'promise',
      message,
      reason,
      stack: reason instanceof Error ? (reason.stack ?? null) : null
    }
    if (isNoise(normalized)) return

    console.error('Unhandled Promise Rejection:', reason)

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = reason instanceof Error ? reason : new Error(message)
      errorToReport.name = 'UnhandledPromiseRejection'

      this.newRelicInstance.noticeError(errorToReport, {
        originalReason: message,
        ...customAttributes
      })
    }
  }

  reportWindowError(
    message: string | Event,
    url?: string,
    line?: number,
    col?: number,
    err?: Error,
    customAttributes?: Record<string, unknown>
  ) {
    const messageText = err?.message ?? (typeof message === 'string' ? message : '')
    const normalized: NormalizedError = {
      source: 'window',
      message: messageText,
      reason: err,
      stack: err?.stack ?? null,
      url,
      line
    }
    if (isNoise(normalized)) return

    console.error('JS Error:', err || message)
    if (url) console.error('URL:', url)
    if (line !== undefined) console.error('Line:', line, 'Column:', col)

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = err || new Error(String(message))
      this.newRelicInstance.noticeError(errorToReport, {
        errorUrl: url,
        errorLine: line,
        errorColumn: col,
        ...customAttributes
      })
    }
  }

  reportError(err: unknown, customAttributes?: Record<string, unknown>) {
    console.error('JS Error:', err)

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = err instanceof Error ? err : new Error(String(err))
      this.newRelicInstance.noticeError(errorToReport, {
        ...customAttributes
      })
    }
  }

  reportAxiosError(err: AxiosError, customAttributes?: Record<string, unknown>) {
    const responseData: unknown = err.response?.data
    const statusCode = err.response?.status
    const requestUrl = err.config?.url
    const errorMessage = this.getAxiosErrorMessage(responseData, statusCode, err.message)
    const normalized: NormalizedError = {
      source: 'axios',
      message: errorMessage,
      reason: err,
      stack: err.stack ?? null,
      axiosCode: err.code,
      axiosStatus: statusCode,
      axiosOriginalMessage: err.message
    }
    if (isNoise(normalized)) return

    console.error('Axios Error:', err)
    console.error('Error Message:', errorMessage)

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = new Error(errorMessage)
      errorToReport.name = 'AxiosError'

      this.newRelicInstance.noticeError(errorToReport, {
        originalMessage: err.message,
        statusCode,
        requestUrl,
        responseData:
          typeof responseData === 'string' ? responseData : JSON.stringify(responseData),
        ...customAttributes
      })
    }
  }

  private getAxiosErrorMessage(
    responseData: unknown,
    statusCode: number | undefined,
    fallbackMessage: string
  ): string {
    let apiErrorMessage: string | null = null
    if (typeof responseData === 'string' && responseData.length > 0) {
      apiErrorMessage = responseData
    } else if (responseData && typeof responseData === 'object') {
      // Common API response formats: { message: "..." }, { error: "..." }, { Message: "..." }
      const data = responseData as Record<string, unknown>
      if (typeof data.message === 'string' && data.message) apiErrorMessage = data.message
      else if (typeof data.error === 'string' && data.error) apiErrorMessage = data.error
      else if (typeof data.Message === 'string' && data.Message) apiErrorMessage = data.Message
    }

    if (apiErrorMessage) return apiErrorMessage
    if (statusCode) return `HTTP ${statusCode}: ${fallbackMessage}`
    return fallbackMessage
  }

  reportVueRouterError(err: unknown, customAttributes?: Record<string, unknown>) {
    console.error('Vue Router Error:', err)

    if (this.reportToNewRelic && this.newRelicInstance) {
      const errorToReport = err instanceof Error ? err : new Error(String(err))
      this.newRelicInstance.noticeError(errorToReport, {
        ...customAttributes
      })
    }
  }
}

export const appErrorService = AppErrorService.getInstance()
